/** Gap-free header panels: hover and click share one state machine. */
const desktop = () => matchMedia('(min-width: 1024px)').matches;
const CLOSE_DELAY = 140;
const CLOSE_DURATION = 150;

let activePanel = null;
let activeTrigger = null;
let closeTimer = 0;
const panelTriggers = new WeakMap();

const triggersFor = (name) => document.querySelectorAll(`[data-panel="${name}"]`);

function positionPanel(panel, trigger) {
  if (!desktop() || !panel.classList.contains('overlay--dropdown')) return;
  const header = trigger.closest('.header');
  panel.style.setProperty('--panel-top', `${Math.round(header.getBoundingClientRect().bottom)}px`);
}

function setExpanded(name, value) {
  triggersFor(name).forEach((trigger) => trigger.setAttribute('aria-expanded', String(value)));
}

function finishClose(panel, trigger) {
  if (!panel.open) return;
  panel.close();
  panel.removeAttribute('data-closing');
  if (trigger) setExpanded(trigger.dataset.panel, false);
  if (activePanel === panel) {
    activePanel = null;
    activeTrigger = null;
  }
}

function closePanel(panel = activePanel, immediate = false) {
  clearTimeout(closeTimer);
  if (!panel?.open) return;
  const trigger = activeTrigger;
  if (immediate || matchMedia('(prefers-reduced-motion: reduce)').matches) {
    finishClose(panel, trigger);
    return;
  }
  panel.setAttribute('data-closing', '');
  window.setTimeout(() => finishClose(panel, trigger), CLOSE_DURATION);
}

function queueClose() {
  clearTimeout(closeTimer);
  closeTimer = window.setTimeout(() => closePanel(), CLOSE_DELAY);
}

function cancelClose() {
  clearTimeout(closeTimer);
}

function openPanel(trigger, { keepFocus = true } = {}) {
  const panel = document.getElementById(`panel-${trigger.dataset.panel}`);
  if (!panel) return;
  cancelClose();
  if (activePanel === panel && panel.open) return;
  if (activePanel?.open) closePanel(activePanel, true);

  activePanel = panel;
  activeTrigger = trigger;
  panelTriggers.set(panel, trigger);
  panel.removeAttribute('data-closing');
  positionPanel(panel, trigger);
  if (desktop() && panel.classList.contains('overlay--dropdown')) panel.show();
  else panel.showModal();
  /* A dialog hands focus to its first link as it opens. Nobody asked for that
     when the panel opened under the pointer, and the browser then paints the
     keyboard focus ring on a link the visitor never reached — the stray
     underline under the first item in every dropdown. Hand focus back. The
     keyboard path focuses deliberately, after this, and is untouched. */
  if (!keepFocus && panel.contains(document.activeElement)) document.activeElement.blur();
  setExpanded(trigger.dataset.panel, true);
  if (trigger.dataset.panel === 'search') {
    requestAnimationFrame(() => panel.querySelector('[data-search-input]')?.focus({ preventScroll: true }));
  }
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-panel]');
  if (trigger) {
    if (desktop() && trigger.matches('a[data-panel-link]')) return;
    const panel = document.getElementById(`panel-${trigger.dataset.panel}`);
    if (!panel) return;
    event.preventDefault();
    if (activePanel === panel && panel.open) {
      if (!desktop()) closePanel();
    } else {
      openPanel(trigger);
    }
    return;
  }

  const close = event.target.closest('[data-panel-close]');
  if (close) {
    closePanel(close.closest('dialog'));
    return;
  }

  if (activePanel?.open && desktop() && !event.target.closest('.overlay--dropdown')) closePanel();
  else if (event.target.matches('dialog.overlay')) closePanel(event.target);
});

document.addEventListener('pointerover', (event) => {
  if (!desktop()) return;
  const trigger = event.target.closest('.header__nav-item[data-panel]');
  if (trigger) openPanel(trigger, { keepFocus: false });
  if (event.target.closest('.overlay--dropdown, .header__nav')) cancelClose();
});

document.addEventListener('pointerout', (event) => {
  if (!desktop()) return;
  const from = event.target.closest('.overlay--dropdown, .header__nav');
  if (!from) return;
  const to = event.relatedTarget?.closest?.('.overlay--dropdown, .header__nav');
  if (!to) queueClose();
});

document.addEventListener('keydown', (event) => {
  const trigger = event.target.closest('.header__nav-item[data-panel]');
  if (desktop() && trigger && event.key === 'ArrowDown') {
    event.preventDefault();
    openPanel(trigger);
    document.getElementById(`panel-${trigger.dataset.panel}`)?.querySelector('a, button')?.focus();
    return;
  }
  if (event.key === 'Escape' && activePanel?.open) closePanel();
});

document.addEventListener('close', (event) => {
  if (!(event.target instanceof HTMLDialogElement)) return;
  const trigger = panelTriggers.get(event.target);
  if (trigger) setExpanded(trigger.dataset.panel, false);
  if (!desktop()) trigger?.focus();
  if (activePanel === event.target) {
    activePanel = null;
    activeTrigger = null;
  }
}, true);

addEventListener('resize', () => {
  if (activePanel?.open && activeTrigger) positionPanel(activePanel, activeTrigger);
});
